import type { CSSProperties } from 'react'
import { useLocale } from '../i18n/context'

/**
 * "The list is not showing the beginning of the deck", and the way back.
 *
 * §21.3 buys the single-request deep link by MOVING the window rather than
 * paging up to it, and a moved window the reader can neither see nor undo is a
 * worse trade than the chain it replaced. This is that disclosure.
 *
 * One component, mounted by both surfaces. It began as the phone feed's own
 * markup; the desktop list needed the same sentence and the same exit, and a
 * second copy of a sentence is how two surfaces start saying slightly different
 * things about the same state. The caller decides WHEN to show it (each feed
 * has its own modes to hide it under — select mode and search on the phone, a
 * running search on the desktop) and supplies the anchor; the wording, the
 * arithmetic from anchor to card number, and the exit are here.
 */
export function DeckAnchorNotice({ anchorPage, pageSize, onBackToStart }: {
  anchorPage: number
  pageSize: number
  onBackToStart: () => void
}) {
  const { tr } = useLocale()
  if (anchorPage <= 1) return null
  return (
    <div style={noticeStyle}>
      <span style={{ flex: 1 }}>
        {tr.deckManagement.anchoredFrom((anchorPage - 1) * pageSize + 1)}
      </span>
      <button onClick={onBackToStart} style={resetStyle}>
        {tr.deckManagement.anchoredBackToStart}
      </button>
    </div>
  )
}

// Says the window moved, in the same register as every other explanatory line
// on these surfaces: a plain sentence and the action that undoes it. Not a
// warning — nothing went wrong, the reader asked to be here.
const noticeStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '8px 12px', marginBottom: 6,
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  fontFamily: 'var(--font-ui)', fontSize: 12, color: 'var(--text-secondary)',
}

const resetStyle: CSSProperties = {
  flexShrink: 0,
  background: 'transparent', border: 'none', color: 'var(--accent)',
  fontFamily: 'var(--font-ui)', fontWeight: 600, fontSize: 12,
  cursor: 'pointer', padding: 0, textDecoration: 'underline',
}
